import { forwardRef } from "react";
import { Smile, Meh, Frown } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * SentimentIndicator — xabar yoki suhbat sentiment darajasi.
 * Manba: chatbot-validator agent natijasi (sentiment: -1..1).
 *
 * Threshold:
 *   sentiment >= 0.25  → positive
 *   sentiment <= -0.25 → negative
 *   else               → neutral
 */

export type SentimentKind = "positive" | "neutral" | "negative";

interface Meta {
  label: string;
  icon: typeof Smile;
  cls: string;
}

const META: Record<SentimentKind, Meta> = {
  positive: {
    label: "Ijobiy",
    icon: Smile,
    cls: "text-[var(--sentiment-positive-fg)] bg-[var(--sentiment-positive-bg)] border-[var(--sentiment-positive-border)]",
  },
  neutral: {
    label: "Neytral",
    icon: Meh,
    cls: "text-[var(--sentiment-neutral-fg)] bg-[var(--sentiment-neutral-bg)] border-[var(--sentiment-neutral-border)]",
  },
  negative: {
    label: "Salbiy",
    icon: Frown,
    cls: "text-[var(--sentiment-negative-fg)] bg-[var(--sentiment-negative-bg)] border-[var(--sentiment-negative-border)]",
  },
};

export function classifySentiment(value: number | null | undefined): SentimentKind {
  if (value == null || Number.isNaN(value)) return "neutral";
  if (value >= 0.25) return "positive";
  if (value <= -0.25) return "negative";
  return "neutral";
}

const sizeMap = {
  sm: { wrap: "h-4 px-1.5 text-[10px] gap-1", icon: "h-3 w-3" },
  md: { wrap: "h-5 px-2 text-[11px] gap-1.5", icon: "h-3.5 w-3.5" },
  lg: { wrap: "h-6 px-2.5 text-xs gap-1.5", icon: "h-4 w-4" },
} as const;

export interface SentimentIndicatorProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** -1..1 raqam yoki to'g'ridan-to'g'ri kategoriya */
  value: number | SentimentKind | null | undefined;
  size?: "sm" | "md" | "lg";
  /** Faqat ikonka (label yashirin) */
  iconOnly?: boolean;
  /** Raqam ko'rsatish (qavs ichida) */
  showScore?: boolean;
}

export const SentimentIndicator = forwardRef<
  HTMLSpanElement,
  SentimentIndicatorProps
>(({ value, size = "md", iconOnly = false, showScore = false, className, ...props }, ref) => {
  const kind: SentimentKind =
    typeof value === "string"
      ? (value as SentimentKind)
      : classifySentiment(typeof value === "number" ? value : null);
  const meta = META[kind];
  const Icon = meta.icon;
  const s = sizeMap[size];
  const numeric = typeof value === "number" ? value : null;
  const titleText =
    numeric != null ? `${meta.label} (${numeric.toFixed(2)})` : meta.label;
  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center rounded-full border font-medium leading-none whitespace-nowrap",
        s.wrap,
        meta.cls,
        iconOnly && "!px-0 aspect-square justify-center",
        className,
      )}
      title={titleText}
      {...props}
    >
      <Icon className={s.icon} aria-hidden />
      {!iconOnly && (
        <span>
          {meta.label}
          {showScore && numeric != null && (
            <span className="ml-1 font-mono opacity-70">
              {numeric > 0 ? "+" : ""}
              {numeric.toFixed(2)}
            </span>
          )}
        </span>
      )}
    </span>
  );
});
SentimentIndicator.displayName = "SentimentIndicator";
